import React, { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { X, Check, Tag } from "lucide-react";
import { Label } from "@/components/ui/label";

interface SelectOption {
  id: number;
  value: string;
  label: string;
}

interface MultiSelectFieldProps {
  id: string;
  label: string;
  selectedValues: string[];
  onValueChange: (value: string) => void;
  onRemove: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  required?: boolean;
  isValid?: boolean;
  formSubmitted?: boolean;
  errorMessage?: string;
  className?: string;
  icon?: React.ReactNode;
}

const MultiSelectField = ({
  id,
  label,
  selectedValues,
  onValueChange,
  onRemove,
  options,
  placeholder = "Seleccionar...",
  required = false,
  isValid = true,
  formSubmitted = false,
  errorMessage = "Este campo es requerido",
  className = "",
  icon,
}: MultiSelectFieldProps) => {
  const [isFocused, setIsFocused] = useState(false);
  
  return (
    <div className={`relative group ${className}`}>
      <Label 
        htmlFor={id} 
        className={`block text-sm font-medium mb-2 transition-colors duration-200 ${
          !isValid && formSubmitted 
            ? "text-red-500" 
            : isFocused 
              ? "text-blue-600" 
              : "text-gray-700"
        }`}
      >
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      <div className="relative">
        {icon ? icon : (
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
            <Tag className={`h-5 w-5 ${
              !isValid && formSubmitted 
                ? "text-red-500" 
                : isFocused 
                  ? "text-blue-600" 
                  : "text-gray-400"
            }`} />
          </div>
        )}
        <Select
          value=""
          onValueChange={onValueChange}
          onOpenChange={(open) => setIsFocused(open)}
        >
          <SelectTrigger 
            className={`w-full pl-12 rounded-lg border ${
              !isValid && formSubmitted 
                ? "border-red-500 focus-visible:ring-red-500" 
                : isFocused 
                  ? "border-blue-500 focus-visible:ring-blue-500" 
                  : "border-gray-300"
            } shadow-sm h-12 text-sm transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-50 group-hover:border-gray-400`}
          >
            <SelectValue placeholder={<span className="text-sm text-gray-400">{placeholder}</span>} />
          </SelectTrigger>
          <SelectContent 
            position="popper" 
            className="rounded-lg border border-gray-200 shadow-md z-[9999999]"
            sideOffset={5}
          >
            {options.map((option) => (
              <SelectItem
                key={option.id}
                value={option.value}
                className="text-sm py-2.5 cursor-pointer"
              >
                <div className="flex items-center justify-between w-full">
                  <span>{option.label}</span>
                  {selectedValues.includes(option.value) && (
                    <Check className="w-4 h-4 text-blue-600" />
                  )}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {selectedValues.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {selectedValues.map((value) => (
            <span
              key={value}
              className="bg-blue-50 text-blue-700 text-sm font-medium px-3 py-1.5 rounded-full flex items-center gap-1 border border-blue-100 transition-all duration-200"
            >
              {value}
              <button
                onClick={() => onRemove(value)}
                className="ml-1 p-0.5 rounded-full hover:bg-blue-100 transition-colors duration-200 flex items-center justify-center"
                type="button"
                aria-label={`Eliminar ${value}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}
      
      {!isValid && formSubmitted && (
        <p className="text-red-500 text-xs mt-2 flex items-center">
          <span className="inline-block mr-1">•</span>
          {errorMessage}
        </p>
      )}
    </div>
  );
};

export default MultiSelectField; 